import {OverlayModule, OverlayRef} from '@angular/cdk/overlay';
import {PortalModule} from '@angular/cdk/portal';
import {CommonModule} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  Output,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule, MatMenuTrigger} from '@angular/material/menu';
import {MatToolbarModule} from '@angular/material/toolbar';
import {ActivationEnd, Router, RouterModule} from '@angular/router';
import {PrivateDataEnclaveComponent} from '@app/private-data-enclave/private-data-enclave.component';
import {PublicDataCommonsComponent} from '@app/public-data-commons/public-data-commons.component';
import {ResourceExplorerComponent} from '@app/resource-explorer/resource-explorer.component';
import {AuthenticationService} from '@authentication/authentication.service';
import {InstanceUIConfigResponse, InstanceUserRoleID, OrganizationUIConfigSchema, UiConfigService} from '@middleware';
import {AuthDataService} from '@services/authentication/authentication-data.service';
import {OrgService} from '@services/organization/org.service';
import {
  SectionHeaderAction,
  SectionHeaderActions,
} from '@shared/components/section-header-actions/section-header-actions';
import {TimeLeftPipe} from '@shared/pipes/time-left/time-left.pipe';
import {filter} from 'rxjs/operators';

type InstanceUIConfigSchema = InstanceUIConfigResponse['config_data'];

interface ImageInfo {
  src: string;
  alt: string;
}

@Component({
  standalone: true,
  selector: 'header.app-header',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatToolbarModule,
    OverlayModule,
    PortalModule,
    RouterModule,
    TimeLeftPipe,
    CommonModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements SectionHeaderActions {
  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
  public sectionTitle = signal('');
  public showSectionHeader = signal(false);
  public showSectionShield = signal(false);
  public userSignal = computed(() => this.authData.me());
  public isUserAdmin = computed(() => this.authData.me()?.instance_user_role_id === InstanceUserRoleID.ADMIN);
  public tokenValidUntil: Date;

  public sectionUrl = signal('');
  public isSearchPage = signal(false);

  configData: WritableSignal<InstanceUIConfigSchema> = signal(undefined);
  orgConfigData: WritableSignal<OrganizationUIConfigSchema> = signal(undefined);
  logoImgInfo: Signal<ImageInfo> = computed(() => {
    const configData = this.configData();
    return {
      src:
        configData?.graphics?.header_image ??
        'https://ithriv-media-static.s3.amazonaws.com/ithriv/institution_logos/uvahealth-logo.png',
      alt: configData?.name ?? 'UVA Health',
    };
  });

  protected overlay: OverlayRef;
  private readonly authData = inject(AuthDataService);
  @Output() public hamburgerRequested = new EventEmitter<void>();
  readonly #searchComponents = [ResourceExplorerComponent, PublicDataCommonsComponent, PrivateDataEnclaveComponent];
  private currentComponent: WritableSignal<any> = signal(undefined);
  public sectionHeaderActions: Signal<SectionHeaderAction[]> = computed(() => {
    const component = this.currentComponent();
    const actions = component?.sectionHeaderActions?.();
    if (!actions) return [];
    return actions.map(action => ({
      ariaLabel: action.caption === 'Help' ? 'Section Help' : undefined,
      ...action,
    }));
  });

  constructor(
    protected readonly router: Router,
    readonly orgService: OrgService,
    private readonly authService: AuthenticationService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly uiConfigService: UiConfigService,
  ) {
    this.uiConfigService.getInstanceUIConfig().subscribe(v => this.configData.set(v.config_data));

    // Update header info on route changes
    this.router.events
      .pipe(
        filter(e => e instanceof ActivationEnd),
        filter(e => e.snapshot.firstChild === null),
      )
      .subscribe(e => {
        const d = e.snapshot.data;
        const segments = e.snapshot.pathFromRoot.flatMap(s => s.url).map(segment => segment.path);
        const firstSegment = segments.length > 0 ? `/${segments[0]}` : '/'; // Top-level section

        this.sectionTitle.set(d['siteHeaderSection'] ?? '');

        this.showSectionHeader.set(!(d['siteHeaderSuppress'] ?? false));
        this.showSectionShield.set(d['siteHeaderShield'] ?? false);
        this.sectionUrl.set(firstSegment);
      });

    // Reactive effect for user login/logout
    effect(() => {
      const u = this.userSignal();
      const orgMap = this.orgService.organizationMap();
      if (u && orgMap) {
        const org = orgMap[u.organization.id];
        this.orgConfigData.set(org?.config_data);
      }

      this.tokenValidUntil = this.authService.expires();
      this.changeDetector.markForCheck();
    });
  }

  public onActivate(component: any) {
    this.isSearchPage.set(!!(component && this.#searchComponents.some(klazz => component instanceof klazz)));
    this.currentComponent.set(component);
  }

  public get tokenValidityPeriod() {
    return this.tokenValidUntil ? this.tokenValidUntil.getTime() - Date.now() : 0;
  }

  public get tokenValidityPeriodLow() {
    return this.tokenValidityPeriod < 900_000;
  }
}
